import React from 'react'
import { TickmillCompanyDto } from '@/api/Identity/Types/Company'
import { CountryDto } from '@/api/Identity/Types/Country'
import { WalletTypeDto } from '@/api/Wallet/Types/WalletTypeDto'
import { Button } from '@/components/ui/button'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'
import {
  FormBaseButtonContainer,
  FormBaseContainer,
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

export interface AddInterestRateFormValues {
  id?: string
  tickmillCompanyId: number
  countryId: string
  currencyId: string
  walletTypeId: number
  enabled: boolean
}

const AddInterestRateFormUI: React.FC<FormikProps<AddInterestRateFormValues> & OuterProps> = (
  props
) => {
  const { handleSubmit, setSubmitting, isSubmitting, errors } = props
  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <FormBaseContainer>
      <Form>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Tickmill Company</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field component='select' name='tickmillCompanyId'>
                <option key={0} value={undefined}>
                  None
                </option>
                {props.tickmillCompanies.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Field>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.tickmillCompanyId && (
            <FormBaseErrorMessage>{errors.tickmillCompanyId}</FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Country</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field name='countryId' component='select' data-test='country-field'>
                <option key={0} value={undefined}>
                  None
                </option>
                {props.countries.map((x) => (
                  <option value={x.id} key={x.id}>
                    {x.name}
                  </option>
                ))}
              </Field>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.countryId && <FormBaseErrorMessage>{errors.countryId}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Currency</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field component='select' name='currencyId'>
                {(props.currencies || []).map((currency) => (
                  <option key={currency.id} value={currency.id}>
                    {currency.id}
                  </option>
                ))}
              </Field>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.currencyId && <FormBaseErrorMessage>{errors.currencyId}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Wallet Type</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field as='select' name='walletTypeId' autoComplete='off'>
                <option key={0} value={undefined}>
                  None
                </option>
                {(props.walletTypes || []).map((walletType) => (
                  <option key={walletType.id} value={walletType.id}>
                    {walletType.name}
                  </option>
                ))}
              </Field>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.walletTypeId && (
            <FormBaseErrorMessage>{errors.walletTypeId}</FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Status</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field name='enabled' type='checkbox' />
            </FormBaseFieldGroup>
          </FormBaseRow>
        </FormBaseFieldBlock>
        <FormBaseButtonContainer>
          <Button disabled={isSubmitting} key='button' onClick={handleSubmitForm} type='button'>
            Submit
          </Button>
        </FormBaseButtonContainer>
      </Form>
    </FormBaseContainer>
  )
}

interface OuterProps {
  permissions: PermissionDto[]
  tickmillCompanies: TickmillCompanyDto[]
  countries: CountryDto[]
  currencies: WalletCurrencyDto[]
  walletTypes: WalletTypeDto[]

  onSubmit(values: FormSubmitValues<AddInterestRateFormValues>): Promise<void>
}

function dataToSubmit(
  values: AddInterestRateFormValues
): FormSubmitValues<AddInterestRateFormValues> {
  return {
    ...values,
    tickmillCompanyId: Number(values.tickmillCompanyId),
    walletTypeId: Number(values.walletTypeId),
  }
}

export const AddInterestRateForm = withFormik<OuterProps, AddInterestRateFormValues>({
  mapPropsToValues: () => {
    return {
      tickmillCompanyId: 0,
      countryId: '',
      currencyId: 'USD',
      walletTypeId: 0,
      enabled: true,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddInterestRateFormValues> = {}
    if (!values.countryId) {
      errors.countryId = 'required'
    }
    if (!values.tickmillCompanyId) {
      errors.tickmillCompanyId = 'required'
    }
    if (!values.walletTypeId) {
      errors.walletTypeId = 'required'
    }
    if (!values.currencyId) {
      errors.currencyId = 'required'
    }
    return errors
  },
  enableReinitialize: true,
})(AddInterestRateFormUI)
